import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { chinaInput } from "./model";

export function useAdvertisingReportFilters() {
  const [q, setQ] = useSearchParams();
  const [submittedClickId, setSubmittedClickId] = useState(() => q.get("click_id") ?? "");
  // Click identifiers stay in this mounted report's memory, never its URL or storage.
  // Import a legacy deep link before replacing it so cleanup cannot broaden the query.
  const clickId = q.has("click_id") ? q.get("click_id") ?? "" : submittedClickId;
  const today = chinaInput(new Date().toISOString()).slice(0, 10);
  const week = chinaInput(new Date(Date.now() - 6 * 86400000).toISOString()).slice(0, 10);
  const publicSubmitted = {
    start_date: q.get("start_date") || week,
    end_date: q.get("end_date") || today,
    ad_id: q.get("ad_id") ?? "",
    agent_id: q.get("agent_id") ?? "",
    placement: q.get("placement") ?? "",
    revision_id: q.get("revision_id") ?? "",
    attribution_status: q.get("attribution_status") ?? "",
    redirect_status: q.get("redirect_status") ?? "",
  };
  const submitted = { ...publicSubmitted, click_id: clickId };
  const [draft, setDraft] = useState(submitted);
  const [version, setVersion] = useState(0);
  const publicFilterKey = JSON.stringify(publicSubmitted);
  useEffect(() => {
    // Back/Forward can change shared filters without remounting this report.
    // Page-only navigation keeps in-progress edits and the private identifier.
    setDraft((p) => ({ ...publicSubmitted, click_id: p.click_id }));
  }, [publicFilterKey]);
  useEffect(() => {
    if (!q.has("click_id")) return;
    const value = q.get("click_id") ?? "";
    setSubmittedClickId(value);
    setDraft((p) => ({ ...p, click_id: value }));
    const next = new URLSearchParams(q);
    next.delete("click_id");
    setQ(next, { replace: true });
  }, [q, setQ]);
  const rawPage = Number(q.get("page"));
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const submit = () => {
    const next = new URLSearchParams(q);
    for (const [key, value] of Object.entries(draft)) {
      if (key === "click_id") continue;
      value ? next.set(key, value) : next.delete(key);
    }
    next.delete("click_id");
    next.delete("page");
    setSubmittedClickId(draft.click_id);
    setQ(next, { replace: true });
    setVersion((v) => v + 1);
  };
  const changePage = (value: number) => {
    const next = new URLSearchParams(q);
    next.delete("click_id");
    next.set("page", String(value));
    setQ(next, { replace: true });
  };
  const publicQuery = new URLSearchParams(q);
  publicQuery.delete("click_id");
  return {
    draft, submitted, page, changePage, submit,
    key: JSON.stringify([publicQuery.toString(), version, clickId]),
    set: (name: keyof typeof draft, value: string) => setDraft((p) => ({ ...p, [name]: value })),
  };
}
